var Control   = require("enyo/Control"),
    Layout    = require("app/ui/Layout"),
    clipText  = require("app/utils/clipText"),
    dom       = require("enyo/dom"),
    get       = require("object-path").get,
    kind      = require("enyo/kind"),
    lerp      = require("app/utils/lerp"),
    themeVars = require("app/theme/variables");

module.exports = kind(/** @lends  app.ArtistPage.HeaderContentLayout.prototype */ {

    /**
     * @private
     */
    name: "app.ArtistPage.HeaderContentLayout",

    /**
     * @private
     */
    kind: Layout,

    /**
     * @public
     */
    reflow: function() {
        var $container         = this.container,
            showMetaText       = get($container, "state.data.show_meta_text", false),
            $title             = $container.$.title,
            $secondaryTitle    = $container.$.secondaryTitle,
            $description       = $container.$.description,
            $mediaButtons      = $container.$.mediaButtons,
            $bookmarkButton1   = $container.$.bookmarkButton1,
            $followerCount     = $container.$.followerCount,
            $tabBar            = $container.$.tabBar,
            $actionButtons     = $container.$.actionButtons,
            $bookmarkButton2   = $container.$.bookmarkButton2,
            $followButton      = $container.$.followButton,
            $shareButton       = $container.$.shareButton,
            containerNode      = $container.hasNode(),
            titleNode          = $title.hasNode(),
            secondaryTitleNode = $secondaryTitle.hasNode(),
            descriptionNode    = $description.hasNode(),
            mediaButtonsNode   = $mediaButtons.hasNode(),
            followerCountNode  = $followerCount.hasNode(),
            tabBarNode         = $tabBar.hasNode(),
            actionButtonsNode  = $actionButtons.hasNode(),
            idealMaxHeight,
            appHeaderOffset,
            containerWidth,
            containerPaddingLeft,
            containerPaddingRight,
            contentTopSpacing,
            applyTopSpacing,
            top,
            titleHeight,
            titleLineHeight,
            maxTitleHeight,
            secondaryTitleHeight,
            secondaryTitleLineHeight,
            maxSecondaryTitleHeight,
            descriptionHeight,
            descriptionLineHeight,
            maxDescriptionHeight,
            mediaButtonsHeight,
            mediaButtonsMarginTop,
            followerCountMarginTop,
            followerCountHeight,
            followerCountMarginBottom,
            actionButtonsHeight,
            actionButtonsWidth,
            actionButtonsMarginTop,
            tabBarHeight,
            leftColHeight,
            rightColHeight,
            nonClipableContentHeight,
            clipableContentHeight,
            excessHeight,
            unclippedHeight,
            clippedHeight,
            heightReduction,
            additionalSpace;

        if (!containerNode) {
            return;
        }

        // Reset
        // --------------------

        // Visually hide things to avoid drawing intermediate stages of the
        // layout process.
        this.visuallyHideControl($title);
        this.visuallyHideControl($secondaryTitle);
        this.visuallyHideControl($description);
        this.visuallyHideControl($mediaButtons);
        this.visuallyHideControl($followerCount);
        this.visuallyHideControl($tabBar);
        this.visuallyHideControl($actionButtons);

        // Unclip everything
        clipText.unclip(titleNode);
        clipText.unclip(secondaryTitleNode);
        clipText.unclip(descriptionNode);
        $description.set("style", descriptionNode.style.cssText);
        $secondaryTitle.set("style", secondaryTitleNode.style.cssText);
        $title.set("style", titleNode.style.cssText);

        // Restore margins
        $secondaryTitle.applyStyle("margin-top", null);
        $description.applyStyle("margin-top", null);
        $mediaButtons.addStyles({
            marginTop: null,
            left:      null,
            right:     null
        });
        $followerCount.addStyles({
            marginTop:    null,
            marginBottom: null,
            left:         null,
            right:        null
        });
        $actionButtons.addStyles({
            marginTop: null,
            left:      null,
            right:     null
        });
        $tabBar.addStyles({
            marginTop: null,
            left:      null,
            right:     null
        });

        // Small screens: no bookmark button in MediaButtons, but in
        // ActionButtons.
        if (["tiny", "smaller", "small"].includes(themeVars.formFactor)) {
            // No captions on ActionButtons
            if (!$bookmarkButton2.hasClass("app-ui-icon-button--no-caption")) {
                $bookmarkButton2.addClass("app-ui-icon-button--no-caption");
            }

            if (!$followButton.hasClass("app-ui-icon-button--no-caption")) {
                $followButton.addClass("app-ui-icon-button--no-caption");
            }

            if (!$shareButton.hasClass("app-ui-icon-button--no-caption")) {
                $shareButton.addClass("app-ui-icon-button--no-caption");
            }

            // Remove Bookmark Button in MediaButtons
            if ($bookmarkButton1.hasNode()) {
                $bookmarkButton1.removeNodeFromDom();
                $bookmarkButton1.teardownRender();
            }
            $bookmarkButton1.set("canGenerate", false);

            // Add Bookmark button in ActionButtons
            $bookmarkButton2.set("canGenerate", true);
            !$bookmarkButton2.hasNode() && $bookmarkButton2.render();
        }

        // Larger screens: no bookmark button in ActionButtons, but in
        // MediaButtons.
        else if (["medium", "large"].includes(themeVars.formFactor)) {
            // Captions on ActionButtons
            while ($bookmarkButton2.hasClass("app-ui-icon-button--no-caption")) {
                $bookmarkButton2.removeClass("app-ui-icon-button--no-caption");
            }

            while ($followButton.hasClass("app-ui-icon-button--no-caption")) {
                $followButton.removeClass("app-ui-icon-button--no-caption");
            }

            while ($shareButton.hasClass("app-ui-icon-button--no-caption")) {
                $shareButton.removeClass("app-ui-icon-button--no-caption");
            }

            // Remove Bookmark Button in ActionButtons
            if ($bookmarkButton2.hasNode()) {
                $bookmarkButton2.removeNodeFromDom();
                $bookmarkButton2.teardownRender();
            }
            $bookmarkButton2.set("canGenerate", false);

            // Add Bookmark button in MediaButtons
            $bookmarkButton1.set("canGenerate", true);
            !$bookmarkButton1.hasNode() && $bookmarkButton1.render();
        }

        // Initial Layout (No clipping)
        // --------------------

        // We calculate the ideal maximum height taken up by all the content
        // except for the tab bar once aligned. While we won't enforce this as
        // a hard limit, we will use it as an aspirational maximum height.
        idealMaxHeight = this.calculateIdealMaxHeight();

        // We may need to make some room for a floating AppHeader.
        appHeaderOffset = this.calculateAppHeaderOffset();

        // Container dimensions
        containerWidth        = containerNode.offsetWidth;
        containerPaddingLeft  = this.getPixelValue(containerNode, "padding-left");
        containerPaddingRight = this.getPixelValue(containerNode, "padding-right");
        contentTopSpacing     = Math.round(lerp(
            themeVars.windowWidth,
            640,
            1280,
            themeVars.getContentPaddingVerticalPx() * 2,
            themeVars.getContentPaddingVerticalPx() * 4
        ));

        // Top alignment of the next item
        top = appHeaderOffset + contentTopSpacing;

        // Layout Title
        titleHeight     = showMetaText ? Math.round(titleNode.offsetHeight) : 0;
        applyTopSpacing = false;

        if (titleHeight) {
            $title.addStyles({
                top:  top + "px",
                left: containerPaddingLeft + "px"
            });
            top             += titleHeight;
            applyTopSpacing = true;
        }

        // Layout Secondary Title
        secondaryTitleHeight = showMetaText ? Math.round(secondaryTitleNode.offsetHeight) : 0;

        if (secondaryTitleHeight) {
            if (applyTopSpacing) {
                top += Math.round(this.getPixelValue(secondaryTitleNode, "margin-top"));
            }
            $secondaryTitle.addStyles({
                top:       top + "px",
                left:      containerPaddingLeft + "px",
                marginTop: "0px"
            });
            top             += secondaryTitleHeight;
            applyTopSpacing = true;
        }

        // Layout Description
        descriptionHeight = ["medium", "large"].includes(themeVars.formFactor) && showMetaText
            ? Math.round(descriptionNode.offsetHeight)
            : 0;

        if (descriptionHeight) {
            if (applyTopSpacing) {
                top += Math.round(this.getPixelValue(descriptionNode, "margin-top"));
            }
            $description.addStyles({
                top:       top + "px",
                left:      containerPaddingLeft + "px",
                marginTop: "0px"
            });
            top             += descriptionHeight;
            applyTopSpacing = true;
        }

        // ActionButtons above TabBar, possibly next to MediaButtons
        if (["tiny", "smaller", "small"].includes(themeVars.formFactor)) {

            // MediaButtons and ActionButtons fit side-by-side
            if ((
                mediaButtonsNode.offsetWidth
                + themeVars.getContentPaddingHorizontalPx() * 1.5
                + Math.max(actionButtonsNode.offsetWidth, followerCountNode.offsetWidth)
            ) <= (
                containerWidth
                - containerPaddingLeft
                - containerPaddingRight
            )) {
                mediaButtonsHeight        = Math.round(mediaButtonsNode.offsetHeight);
                mediaButtonsMarginTop     = mediaButtonsHeight ? Math.round(this.getPixelValue(mediaButtonsNode, "margin-top")) : 0;
                followerCountHeight       = Math.round(followerCountNode.offsetHeight);
                followerCountMarginBottom = followerCountHeight ? Math.round(this.getPixelValue(followerCountNode, "margin-bottom")) : 0;
                actionButtonsHeight       = Math.round(actionButtonsNode.offsetHeight);

                leftColHeight  = mediaButtonsHeight;
                rightColHeight = followerCountHeight + followerCountMarginBottom + actionButtonsHeight;

                if (leftColHeight || rightColHeight) {
                    // We always apply the top margin of the mediaButtons node
                    // above the buttons.
                    if (applyTopSpacing) {
                        top += mediaButtonsMarginTop;
                    }

                    // Layout Media Buttons
                    if (leftColHeight) {
                        top             += Math.max(leftColHeight, rightColHeight) - leftColHeight;
                        $mediaButtons.addStyles({
                            top:       top + "px",
                            left:      containerPaddingLeft + "px",
                            right:     null,
                            marginTop: "0px"
                        });
                        top             += leftColHeight;
                        top             -= Math.max(leftColHeight, rightColHeight);
                        applyTopSpacing = true;
                    }

                    if (rightColHeight) {
                        top += Math.max(leftColHeight, rightColHeight) - rightColHeight;

                        // Layout Follower Count
                        if (followerCountHeight) {
                            $followerCount.addStyles({
                                top:          top + "px",
                                left:         null,
                                right:        containerPaddingRight + "px",
                                marginTop:    "0px",
                                marginBottom: "0px",
                                textAlign:    "right"
                            });
                            top             += followerCountHeight + followerCountMarginBottom;
                            applyTopSpacing = true;
                        }

                        // Layout Action Buttons
                        if (actionButtonsHeight) {
                            $actionButtons.addStyles({
                                top:       top + "px",
                                left:      null,
                                right:     containerPaddingRight + "px",
                                marginTop: "0px"
                            });
                            top             += actionButtonsHeight;
                            applyTopSpacing = true;
                        }
                    }
                }
            }

            // ActionButtons below MediaButtons
            else {
                // Layout Media Buttons
                mediaButtonsHeight    = Math.round(mediaButtonsNode.offsetHeight);
                mediaButtonsMarginTop = mediaButtonsHeight ? Math.round(this.getPixelValue(mediaButtonsNode, "margin-top")) : 0;

                if (mediaButtonsHeight) {
                    if (applyTopSpacing) {
                        top += mediaButtonsMarginTop;
                    }
                    $mediaButtons.addStyles({
                        top:       top + "px",
                        left:      containerPaddingLeft + "px",
                        right:     containerPaddingRight + "px",
                        marginTop: "0px"
                    });
                    top             += mediaButtonsHeight;
                    applyTopSpacing = true;
                }

                // Layout Follower Count
                followerCountHeight       = Math.round(followerCountNode.offsetHeight);
                followerCountMarginBottom = Math.round(this.getPixelValue(followerCountNode, "margin-bottom"));

                if (followerCountHeight) {
                    if (!mediaButtonsHeight) {
                        // Initially the first item directly below the metadata will
                        // have the MediaButtons top margin.
                        followerCountMarginTop = mediaButtonsMarginTop;
                    }
                    else {
                        followerCountMarginTop = Math.round(this.getPixelValue(followerCountNode, "margin-top"));
                    }
                }
                else {
                    followerCountMarginTop = 0;
                }

                if (followerCountHeight) {
                    if (applyTopSpacing) {
                        top += followerCountMarginTop;
                    }
                    $followerCount.addStyles({
                        top:          top + "px",
                        left:         containerPaddingLeft + "px",
                        right:        containerPaddingRight + "px",
                        marginTop:    "0px",
                        marginBottom: "0px",
                        textAlign:    "right"
                    });
                    top             += followerCountHeight;
                    applyTopSpacing = true;
                }

                // Layout Action Buttons
                actionButtonsHeight = Math.round(actionButtonsNode.offsetHeight);

                if (actionButtonsHeight) {
                    if (!mediaButtonsHeight && !followerCountHeight) {
                        // Initially the first item directly below the metadata will
                        // have the MediaButtons top margin.
                        actionButtonsMarginTop = mediaButtonsMarginTop;
                    }
                    else {
                        actionButtonsMarginTop = followerCountMarginBottom;
                    }
                }
                else {
                    actionButtonsMarginTop = 0;
                }

                if (actionButtonsHeight) {
                    if (applyTopSpacing) {
                        top += actionButtonsMarginTop;
                    }
                    $actionButtons.addStyles({
                        top:       top + "px",
                        left:      containerPaddingLeft + "px",
                        right:     containerPaddingRight + "px",
                        marginTop: "0px"
                    });
                    top             += actionButtonsHeight;
                    applyTopSpacing = true;
                }
            }

            // Layout TabBar
            tabBarHeight = Math.round(tabBarNode.offsetHeight);

            if (tabBarHeight) {
                if (applyTopSpacing) {
                    top += Math.round(this.getPixelValue(tabBarNode, "margin-top"));
                }
                $tabBar.addStyles({
                    top:       top + "px",
                    left:      containerPaddingLeft + "px",
                    right:     containerPaddingRight + "px",
                    marginTop: "0px"
                });
                top             += tabBarHeight;
                applyTopSpacing = true;
            }
        }

        // ActionButtons next to TabBar
        else if (["medium", "large"].includes(themeVars.formFactor)) {

            // Layout Media Buttons
            mediaButtonsHeight    = Math.round(mediaButtonsNode.offsetHeight);
            mediaButtonsMarginTop = mediaButtonsHeight ? Math.round(this.getPixelValue(mediaButtonsNode, "margin-top")) : 0;

            if (mediaButtonsHeight) {
                if (applyTopSpacing) {
                    top += mediaButtonsMarginTop;
                }
                $mediaButtons.addStyles({
                    top:       top + "px",
                    left:      containerPaddingLeft + "px",
                    right:     containerPaddingRight + "px",
                    marginTop: "0px"
                });
                top             += mediaButtonsHeight;
                applyTopSpacing = true;
            }

            // Layout Follower Count
            followerCountHeight = Math.round(followerCountNode.offsetHeight);

            if (followerCountHeight) {
                if (!mediaButtonsHeight) {
                    // Initially the first item directly below the metadata will
                    // have the MediaButtons top margin.
                    followerCountMarginTop = mediaButtonsMarginTop;
                }
                else {
                    followerCountMarginTop = Math.round(this.getPixelValue(followerCountNode, "margin-top"));
                }
            }
            else {
                followerCountMarginTop = 0;
            }

            if (followerCountHeight) {
                if (applyTopSpacing) {
                    top += followerCountMarginTop;
                }
                $followerCount.addStyles({
                    top:          top + "px",
                    left:         containerPaddingLeft + "px",
                    right:        containerPaddingRight + "px",
                    marginTop:    "0px",
                    marginBottom: "0px",
                    textAlign:    "left"
                });
                top             += followerCountHeight;
                applyTopSpacing = true;
            }

            // Spacing between content above and the TabBar/ActionButtons row
            if (applyTopSpacing) {
                top += Math.round(lerp(
                    themeVars.windowWidth,
                    640,
                    1280,
                    themeVars.getContentPaddingVerticalPx(),
                    themeVars.getContentPaddingVerticalPx() * 3
                ));
            }

            tabBarHeight        = Math.round(tabBarNode.offsetHeight);
            actionButtonsHeight = Math.round(actionButtonsNode.offsetHeight);
            actionButtonsWidth  = actionButtonsHeight ? Math.round(actionButtonsNode.offsetWidth) : 0;

            // Layout TabBar
            if (tabBarHeight) {
                top             += Math.max(tabBarHeight, actionButtonsHeight) - tabBarHeight;
                $tabBar.addStyles({
                    top:   top + "px",
                    left:  containerPaddingLeft + "px",
                    right: (
                        containerPaddingRight
                        + actionButtonsWidth
                        + themeVars.getContentPaddingHorizontalPx() * 1.5
                    ) + "px",
                    marginTop: "0px"
                });
                top             += tabBarHeight;
                top             -= Math.max(tabBarHeight, actionButtonsHeight);
                applyTopSpacing = true;
            }

            // Layout Action Buttons
            if (actionButtonsHeight) {
                top += Math.max(tabBarHeight, actionButtonsHeight) - actionButtonsHeight;
                $actionButtons.addStyles({
                    top:       top + "px",
                    left:      null,
                    right:     containerPaddingRight + "px",
                    marginTop: "0px"
                });
                top += actionButtonsHeight;
            }
        }

        // Initial Content Heights
        // --------------------------------

        if (["tiny", "smaller", "small"].includes(themeVars.formFactor)) {
            nonClipableContentHeight = tabBarHeight;
        }
        else if (["medium", "large"].includes(themeVars.formFactor)) {
            nonClipableContentHeight = Math.max(tabBarHeight, actionButtonsHeight);
        }

        clipableContentHeight = top - nonClipableContentHeight;

        // Clipping
        // --------------------------------

        // Crop description if needed (description is hidden on smaller screens)
        if (["medium", "large"].includes(themeVars.formFactor) && clipableContentHeight > idealMaxHeight) {
            excessHeight = clipableContentHeight - idealMaxHeight;

            descriptionLineHeight = this.getPixelValue(descriptionNode, "line-height");

            // We want to remove as much of the excess height from the
            // description as possible, while leaving room for at least 2 lines
            // of description text.
            maxDescriptionHeight = Math.max(
                descriptionHeight - excessHeight,
                Math.ceil(descriptionLineHeight * 2)
            );

            // Clip content and calculate height difference
            unclippedHeight = descriptionNode.offsetHeight;
            clipText.clip(descriptionNode, maxDescriptionHeight, true);
            $description.set("style", descriptionNode.style.cssText);
            clippedHeight   = descriptionNode.offsetHeight;

            // Adjust clipableContentHeight
            heightReduction       = unclippedHeight - clippedHeight;
            clipableContentHeight -= heightReduction;

            // Shift content below
            this.changeNodeTop($mediaButtons, heightReduction * -1);
            this.changeNodeTop($followerCount, heightReduction * -1);
            this.changeNodeTop($actionButtons, heightReduction * -1);
            this.changeNodeTop($tabBar, heightReduction * -1);
        }

        // Crop secondary title if needed
        if (clipableContentHeight > idealMaxHeight) {
            excessHeight = clipableContentHeight - idealMaxHeight;

            secondaryTitleLineHeight = this.getPixelValue(secondaryTitleNode, "line-height");

            // We want to remove as much of the excess height from the secondary
            // title as possible, while leaving room for at least 1 line of
            // secondary title text.
            maxSecondaryTitleHeight = Math.max(
                secondaryTitleHeight - excessHeight,
                Math.ceil(secondaryTitleLineHeight)
            );

            // Clip content and calculate height difference
            unclippedHeight = secondaryTitleNode.offsetHeight;
            clipText.clip(secondaryTitleNode, maxSecondaryTitleHeight, true);
            $secondaryTitle.set("style", secondaryTitleNode.style.cssText);
            clippedHeight   = secondaryTitleNode.offsetHeight;

            // Adjust clipableContentHeight
            heightReduction       = unclippedHeight - clippedHeight;
            clipableContentHeight -= heightReduction;

            // Shift content below
            this.changeNodeTop($description, heightReduction * -1);
            this.changeNodeTop($mediaButtons, heightReduction * -1);
            this.changeNodeTop($followerCount, heightReduction * -1);
            this.changeNodeTop($actionButtons, heightReduction * -1);
            this.changeNodeTop($tabBar, heightReduction * -1);
        }

        // Crop title if needed
        if (clipableContentHeight > idealMaxHeight) {
            excessHeight = clipableContentHeight - idealMaxHeight;

            titleLineHeight = this.getPixelValue(titleNode, "line-height");

            // We want to remove as much of the excess height from the title as
            // possible, while leaving room for at least 1 line of title text.
            maxTitleHeight = Math.max(
                titleHeight - excessHeight,
                Math.ceil(titleLineHeight)
            );

            // Clip content and calculate height difference
            unclippedHeight = titleNode.offsetHeight;
            clipText.clip(titleNode, maxTitleHeight, true);
            $title.set("style", titleNode.style.cssText);
            clippedHeight   = titleNode.offsetHeight;

            // Adjust clipableContentHeight
            heightReduction       = unclippedHeight - clippedHeight;
            clipableContentHeight -= heightReduction;

            // Shift content below
            this.changeNodeTop($secondaryTitle, heightReduction * -1);
            this.changeNodeTop($description, heightReduction * -1);
            this.changeNodeTop($mediaButtons, heightReduction * -1);
            this.changeNodeTop($followerCount, heightReduction * -1);
            this.changeNodeTop($actionButtons, heightReduction * -1);
            this.changeNodeTop($tabBar, heightReduction * -1);
        }

        // Additional padding if there is extra space
        // --------------------------------

        additionalSpace = clipableContentHeight < idealMaxHeight
            ? Math.round(idealMaxHeight - clipableContentHeight)
            : 0;

        // Metadata will be aligned at the bottom
        if (["tiny", "smaller", "small"].includes(themeVars.formFactor) && additionalSpace) {
            this.changeNodeTop($title, additionalSpace);
            this.changeNodeTop($secondaryTitle, additionalSpace);
            this.changeNodeTop($description, additionalSpace);
            this.changeNodeTop($mediaButtons, additionalSpace);
            this.changeNodeTop($followerCount, additionalSpace);
            this.changeNodeTop($actionButtons, additionalSpace);
            this.changeNodeTop($tabBar, additionalSpace);
        }

        // Metadata will be vertically centered between TabBar/ActionButtons and
        // AppHeader.
        else if (["medium", "large"].includes(themeVars.formFactor) && additionalSpace) {
            this.changeNodeTop($title, Math.round(additionalSpace / 2));
            this.changeNodeTop($secondaryTitle, Math.round(additionalSpace / 2));
            this.changeNodeTop($description, Math.round(additionalSpace / 2));
            this.changeNodeTop($mediaButtons, Math.round(additionalSpace / 2));
            this.changeNodeTop($followerCount, Math.round(additionalSpace / 2));
            this.changeNodeTop($actionButtons, additionalSpace);
            this.changeNodeTop($tabBar, additionalSpace);
        }

        clipableContentHeight += additionalSpace;

        nonClipableContentHeight += themeVars.getContentPaddingVerticalPx() * 1.5;

        // Scale Container
        // --------------------------------

        $container.addStyles({
            height: clipableContentHeight + nonClipableContentHeight + "px"
        });

        // Control Visibility
        // --------------------------------

        titleHeight ? this.showControl($title) : this.hideControl($title);
        secondaryTitleHeight ? this.showControl($secondaryTitle) : this.hideControl($secondaryTitle);
        descriptionHeight ? this.showControl($description) : this.hideControl($description);
        mediaButtonsHeight ? this.showControl($mediaButtons) : this.hideControl($mediaButtons);
        followerCountHeight ? this.showControl($followerCount) : this.hideControl($followerCount);
        actionButtonsHeight ? this.showControl($actionButtons) : this.hideControl($actionButtons);
        tabBarHeight ? this.showControl($tabBar) : this.hideControl($tabBar);

        // Broadcast metrics
        // --------------------------------

        // Report layoutMetrics for other components to use
        $container.set("layoutMetrics", {
            contentHeight: clipableContentHeight + nonClipableContentHeight
        });
    },

    /**
     * Determine the ideal maximum height of the content. We may exceed this if
     * absolutely necessary, but we should strive to stay within it.
     *
     * @returns  {number}  The ideal maximum height
     * @private
     */
    calculateIdealMaxHeight: function() {
        var headerGraphicLayoutMetrics = get(this, "container.headerGraphicLayoutMetrics", {}),
            graphicHeight              = get(headerGraphicLayoutMetrics, "graphicHeight", 0),
            graphicOverflow            = get(headerGraphicLayoutMetrics, "graphicOverflow", 0);

        return graphicHeight - graphicOverflow;
    },

    /**
     * Determine how much space we need to make space for a floating AppHeader.
     *
     * @returns  {number}  The ideal height
     * @private
     */
    calculateAppHeaderOffset: function() {
        var appView            = this.container.app.view,
            headerRegionBounds = appView.get("headerRegionBounds") || {},
            headerRegionHeight = headerRegionBounds.height || 0;

        return themeVars.appHeaderFloat
            ? headerRegionHeight + themeVars.safeAreaInsetTop
            : 0;
    },

    /**
     * Get the value in px oF a CSS property from an enyo.Control or
     * HTMLElement.
     *
     * @param    {enyo.Control|HTMLElement}  control   The control or it's node
     * @param    {string}                    property  The css property
     * @returns  {number}  The value in pixels
     * @private
     */
    getPixelValue: function(control, property) {
        var node;

        if (control instanceof HTMLElement) {
            node = control;
        }
        else if (control instanceof Control) {
            node = control.hasNode();
        }
        else {
            node = null;
        }

        return node
            ? parseFloat(dom.getComputedStyleValue(node, property), 10)
            : 0;
    },

    /**
     * Changes the css 'top' position of the provided Control by a set amount of
     * pixels. Positive values move it down, negative values move it up.
     *
     * @param  {enyo.Control}  control  The control or it's node
     * @param  {int}           delta    The amount of pixels to move
     * @private
     */
    changeNodeTop: function(control, delta) {
        var top;

        top = this.getPixelValue(control, "top");
        control.addStyles({
            top: (top + delta) + "px"
        });
    }

});
